import * as PIXI from "pixi.js";

/**
 * Create a BitmapText value display for a traveling spine.
 * Handles counter-scaling to compensate for spine bone transforms,
 * and auto-fits text within the symbol width.
 *
 * @param {object} textData - { text, fontName, fontSize }
 * @param {Spine} spine - The spine instance the text will be injected into
 * @param {number} symbolWidth - Current symbol cell width (for auto-fit)
 * @param {object} [options]
 * @param {string} [options.boneName="value"] - Bone to read base scale from
 * @param {number} [options.paddingPercent=0.12] - Padding fraction for auto-fit
 * @returns {PIXI.Container} Container with the BitmapText child
 */
export function createValueText(textData, spine, symbolWidth, options = {}) {
  const { boneName = "value", paddingPercent = 0.12 } = options;

  const coinValueContainer = new PIXI.Container();
  coinValueContainer.name = "travel_value_text";
  coinValueContainer.alpha = 1;
  coinValueContainer.zIndex = 1000;

  const bitmapText = new PIXI.BitmapText(textData.text, {
    fontName: textData.fontName,
    fontSize: textData.fontSize,
  });
  bitmapText.anchor.set(0.5);
  bitmapText.roundPixels = true;
  bitmapText.updateText();

  // Two scale factors shrink text inside addSlotObject:
  //   1. spine.scale.x (PIXI scale to fit symbol in grid)
  //   2. bone.a (bone's world transform scale)
  // Counter both by scaling fontSize up by 1/(spineScale * boneBaseScale).
  const spineScale = spine.scale.x || 1;
  spine.update(0);
  const valueBone = spine.skeleton?.findBone(boneName);
  const boneBaseScale = Math.abs(valueBone?.a) || 1;
  const totalScale = spineScale * boneBaseScale;

  if (totalScale && totalScale !== 0) {
    bitmapText.fontSize = bitmapText.fontSize / totalScale;
    bitmapText.updateText();
  }

  // Auto-fit: shrink if text overflows symbol width (in spine-local units)
  const maxTextWidth = totalScale !== 0
    ? (symbolWidth * (1 - paddingPercent * 2)) / totalScale
    : symbolWidth * (1 - paddingPercent * 2);
  if (bitmapText.width > maxTextWidth) {
    const scale = maxTextWidth / bitmapText.width;
    bitmapText.scale.set(scale);
  }

  coinValueContainer.addChild(bitmapText);

  return coinValueContainer;
}

/**
 * Inject a container into a spine's named slot.
 * Uses spine's addSlotObject so the container follows slot transforms automatically.
 *
 * @param {Spine} spine - Target spine instance
 * @param {string} slotName - Spine slot name (e.g., "txt_slot_value")
 * @param {PIXI.Container} container - Container to inject
 */
export function injectIntoSlot(spine, slotName, container) {
  if (!spine || !slotName || !container) return;
  spine.addSlotObject(slotName, container);
}
